import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const ServerCompletionDetails = ({ completion, clientId }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Ne pas afficher si pas d'analyse serveur ou pas de problèmes
  if (!completion.serverAnalysis || completion.serverAnalysis.incompleteServers.length === 0) {
    return null;
  }

  const { serverAnalysis } = completion;
  const hasCritical = serverAnalysis.criticalIssues.length > 0;

  return (
    <div className="mt-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          flex items-center space-x-2 text-sm font-medium px-3 py-2 rounded-lg
          transition-colors duration-200 w-full text-left
          ${hasCritical 
            ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' 
            : 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'
          }
        `}
      >
        {hasCritical ? (
          <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
        ) : (
          <InformationCircleIcon className="w-4 h-4 text-orange-500" />
        )}
        
        <span>
          {serverAnalysis.incompleteServers.length} serveur(s) incomplet(s)
          {hasCritical && (
            <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
              Critique
            </span>
          )}
        </span>
        
        <div className="flex-1" />
        
        {isExpanded ? (
          <ChevronUpIcon className="w-4 h-4" />
        ) : (
          <ChevronDownIcon className="w-4 h-4" />
        )}
      </button>

      {isExpanded && (
        <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="p-4">
            <div className="space-y-4">
              {serverAnalysis.incompleteServers.map((server, index) => (
                <div key={index} className="border-l-4 border-gray-300 pl-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">
                      {server.name}
                    </h4>
                    <div className="flex items-center space-x-2">
                      {server.critical.length > 0 && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium">
                          {server.critical.length} critique(s)
                        </span>
                      )}
                      {server.warnings.length > 0 && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                          {server.warnings.length} warning(s)
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Éléments critiques manquants */}
                  {server.critical.length > 0 && (
                    <div className="mb-3">
                      <h5 className="text-sm font-medium text-red-700 mb-1">
                        🚨 Éléments critiques manquants :
                      </h5>
                      <ul className="text-sm text-red-600 space-y-1">
                        {server.critical.map((item, i) => (
                          <li key={i} className="flex items-center space-x-2">
                            <ExclamationTriangleIcon className="w-3 h-3 text-red-500 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Éléments d'avertissement manquants */}
                  {server.warnings.length > 0 && (
                    <div>
                      <h5 className="text-sm font-medium text-orange-700 mb-1">
                        ⚠️ Éléments à compléter :
                      </h5>
                      <ul className="text-sm text-orange-600 space-y-1">
                        {server.warnings.map((item, i) => (
                          <li key={i} className="flex items-center space-x-2">
                            <InformationCircleIcon className="w-3 h-3 text-orange-500 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Actions recommandées */}
                  <div className="mt-3 p-2 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-600">
                      💡 <strong>Action recommandée :</strong> Aller dans les détails du client → 
                      Onglet "Serveurs" → Modifier le serveur "{server.name}" pour compléter les informations manquantes.
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Résumé global */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="bg-blue-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  📊 Résumé d'analyse
                </h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p>• Total serveurs analysés : {serverAnalysis.totalServers}</p>
                  <p>• Serveurs avec problèmes : {serverAnalysis.incompleteServers.length}</p>
                  <p>• Éléments critiques manquants : {serverAnalysis.criticalIssues.length}</p>
                  <p>• Éléments d'avertissement : {serverAnalysis.warningIssues.length}</p>
                  <p>• Score de complétion serveurs : {serverAnalysis.completionScore}%</p>
                </div>
              </div>
            </div>

            {/* Bouton d'action */}
            <div className="mt-4 flex justify-center">
              <button
                onClick={() => window.location.href = `/clients/${clientId}?tab=servers`}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                📝 Modifier les serveurs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerCompletionDetails;
